import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// Import from generated schemas
const {
  CreateCashboxTransactionDto,
  CashboxTransactionFirestoreResponse,
  UpdateCashboxTransactionDto,
} = schemas;

// Re-export schemas for external use
export const CashboxTransactionResponseSchema = CashboxTransactionFirestoreResponse;
export const CreateCashboxTransactionSchema = CreateCashboxTransactionDto;
export const UpdateCashboxTransactionSchema = UpdateCashboxTransactionDto;

export const DeleteResponseSchema = z.object({
  message: z.string(),
}).passthrough();

// ====== Comparison Schema ======
// Note: This is a custom schema not in generated (comparison endpoints are utility)
export const CashboxTransactionComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: CashboxTransactionResponseSchema.nullable(),
}).passthrough();

// Type exports
export type CashboxTransactionResponse = z.infer<typeof CashboxTransactionResponseSchema>;
export type CreateCashboxTransaction = z.infer<typeof CreateCashboxTransactionSchema>;
export type UpdateCashboxTransaction = z.infer<typeof UpdateCashboxTransactionSchema>;
export type CashboxTransactionComparisonResult = z.infer<typeof CashboxTransactionComparisonResultSchema>;

// API factory function
export function createCashboxTransactionsApi(client: TypedApiClient) {
  return {
    getCashboxTransactions: (params?: {
      storeId?: string;
      type?: string;
      startDate?: string;
      endDate?: string;
      /** Defaults to 50 on the backend; a caller that needs every row must page (ATL-1477). */
      limit?: number;
      offset?: number;
    }): Promise<CashboxTransactionResponse[]> => {
      return client.get('/cashbox-transactions', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(CashboxTransactionResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/cashbox-transactions` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getCashboxTransactionsWindow: (
      params: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof CashboxTransactionResponseSchema>>> => {
      return client.get('/cashbox-transactions/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(CashboxTransactionResponseSchema),
      });
    },

    createCashboxTransaction: (data: CreateCashboxTransaction): Promise<CashboxTransactionResponse> => {
      return client.post('/cashbox-transactions', {
        data,
        requestSchema: CreateCashboxTransactionSchema,
        responseSchema: CashboxTransactionResponseSchema,
      });
    },

    updateCashboxTransaction: (id: string, data: UpdateCashboxTransaction): Promise<CashboxTransactionResponse> => {
      return client.patch(apiPath`/cashbox-transactions/${id}`, {
        data,
        requestSchema: UpdateCashboxTransactionSchema,
        responseSchema: CashboxTransactionResponseSchema,
      });
    },

    deleteCashboxTransaction: (id: string): Promise<z.infer<typeof DeleteResponseSchema>> => {
      return client.delete(apiPath`/cashbox-transactions/${id}`, {
        responseSchema: DeleteResponseSchema,
      });
    },

    compareWithFirestore: (firestoreId: string): Promise<CashboxTransactionComparisonResult> => {
      return client.get(apiPath`/cashbox-transactions/compare/${firestoreId}`, {
        responseSchema: CashboxTransactionComparisonResultSchema,
      });
    },

    deleteByFirestoreId: (firestoreId: string) => {
      return client.delete(apiPath`/cashbox-transactions/firestore/${firestoreId}`, {
        responseSchema: z.object({ success: z.boolean() }),
      });
    },
  };
}
